import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import { BoxFinancesPageHeader } from "@/modules/finances/components/financesPageHeader"
import { cn } from "@/modules/lib/utils"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import errorNoRepLight from "@images/error/errorNoRepLight.svg"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { BoxFinancesLiteView } from "@modules/finances/components/boxFinancesLiteView"
import { TaxDialogProvider } from "@modules/finances/taxes/components/taxDialogManager"
import { BoxTransactionsDetailsLayout } from "@modules/finances/transactions/components/boxTransactionDetailsLayout"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { Suspense, useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { Outlet, useLocation } from "react-router-dom"

type BoxFinancesPageProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxFinancesPage({ className, ...props }: BoxFinancesPageProps) {
  const { result: box } = useCurrentBox()
  const boxNavigate = useBoxNavigate()
  const { localStorageService } = useServicesContext()
  const { sidebarNavigateTo } = useSidebar()
  const { pathname } = useLocation()
  const isPayoutsPage = pathname.includes(Path.Box.Finances.Payouts)
  const isPaymentsToValidatePage = pathname.includes(Path.Box.Finances.PaymentsToValidate)
  const isPromoCodesPage = pathname.includes(Path.Box.Finances.PromoCodes)
  const isPayinsPage = !isPayoutsPage && !isPaymentsToValidatePage && !isPromoCodesPage

  const canSeeFinancial = useBoxStaffPermission(BoxStaffPermissions.SEE_FINANCIAL)
  const boxHasStripeAccount = !!box?.stripeAccountId
  const { value: transactionId } = localStorageService.load(LocalStorageKeys.boxTransaction.transactionId, {
    value: "",
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      if (canSeeFinancial && transactionId && transactionId !== "") {
        void sidebarNavigateTo({
          content: (
            <ErrorBoundary fallback={<ErrorView />}>
              <Suspense fallback={<LoadingSpinner />}>
                <BoxTransactionsDetailsLayout transactionId={transactionId} />
              </Suspense>
            </ErrorBoundary>
          ),
          onHide: () => {
            localStorage.removeItem(LocalStorageKeys.boxTransaction.transactionId)
            return Promise.resolve(true)
          },
          replaceAllComponents: true,
        })
      }
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [transactionId, canSeeFinancial])

  if (!canSeeFinancial) {
    boxNavigate(Path.Box.Planning)
  }

  if (!boxHasStripeAccount) return <BoxFinancesLiteView />
  else
    return (
      <TaxDialogProvider>
        <div className={cn("flex w-full grow flex-col gap-6 p-6", className)} {...props}>
          <BoxFinancesPageHeader
            pageName={word("menu.finances")}
            subPageName={
              isPayoutsPage
                ? word("finances.page.payouts.title")
                : isPaymentsToValidatePage
                  ? word("finances.page.paymentToValidate.title")
                  : isPromoCodesPage
                    ? word("finances.page.promoCodes.title")
                    : undefined
            }
            displayMonthPicker={isPayinsPage}
            displaySettingsButton={isPayinsPage}
          />

          <Suspense fallback={<LoadingSpinner />}>
            <ErrorBoundary fallback={<BoxFinanceErrorView />}>
              <Outlet />
            </ErrorBoundary>
          </Suspense>
        </div>
      </TaxDialogProvider>
    )
}

export function BoxFinanceErrorView({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div className={cn("flex h-80 min-h-full w-full flex-col items-center justify-center gap-2", className)} {...props}>
      <Text fontStyle="BM" className="text-textDefault-body">
        {word("global.error.norep")}
      </Text>
      <img src={errorNoRepLight} />
      <Text fontStyle="BM" className="text-textDefault-caption">
        {word("global.error.somethingGoneWrong")}
      </Text>
    </div>
  )
}
